"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const PolicyContext = createContext({
  policies: [],
});

export const PolicyProvider = ({ children }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Policies
  const fetchPolicies = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policies`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch policies");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setPolicies(data.data || []);
      } else {
        setError(data.message || "Failed to fetch policies");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch policies");
    } finally {
      setLoading(false);
    }
  };

  // Create Policy
  const createPolicy = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to create policy",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchPolicies();

        toast.success(data.message || "Policy created successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create policy",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to create policy",
      };
    }
  };

  // Update Policy
  const updatePolicy = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policies/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update policy",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchPolicies();

        toast.success(data.message || "Policy updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update policy",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update policy",
      };
    }
  };

  // Delete Policy
  const deletePolicy = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policies/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete policy",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchPolicies();

        toast.success(data.message || "Policy deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete policy",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete policy",
      };
    }
  };

  // Update Policy Status
  const updateStatus = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/policies/status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchPolicies();

        toast.success(data.message || "Policy status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update status",
      };
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) fetchPolicies();
  }, []);

  return (
    <PolicyContext.Provider
      value={{
        policies,
        loading,
        error,
        fetchPolicies,
        createPolicy,
        updatePolicy,
        deletePolicy,
        updateStatus,
        setPolicies,
      }}
    >
      {children}
    </PolicyContext.Provider>
  );
};

export const usePolicies = () => useContext(PolicyContext);